import { DataScienceModule } from '../../types';

export const ADVANCED_STATS_MODULE: DataScienceModule = {
  id: 'ds-advanced-stats',
  title: 'Advanced Statistics & A/B Testing',
  description: 'Master hypothesis testing, confidence intervals, and A/B testing for business decisions.',
  duration: '2.5 hours',
  difficulty: 'intermediate',
  objectives: [
    'Design and analyze A/B tests',
    'Calculate statistical significance and power',
    'Handle multiple testing problems',
    'Interpret business impact of findings'
  ],
  content: `# Advanced Statistics & A/B Testing

Statistical methods are crucial for data-driven business decisions and hypothesis validation.

## Hypothesis Testing

**Type I Error (α)**: Rejecting a true null hypothesis (false positive).
**Type II Error (β)**: Failing to reject a false null hypothesis (false negative).
**Statistical Power (1-β)**: Probability of detecting a true effect.

## A/B Testing Framework

### Planning Phase
- Define hypothesis and success metrics
- Calculate required sample size
- Determine test duration

### Analysis Phase
- Check assumptions (normality, equal variance)
- Calculate statistical significance
- Estimate confidence intervals

### Common Pitfalls
- **Multiple Testing**: Running many tests increases false positives
- **Selection Bias**: Ensure proper randomization
- **Temporal Effects**: Account for seasonality and trends

## Statistical Tests

- **t-test**: Compare means between two groups
- **Chi-Square**: Test relationships between categorical variables
- **Mann-Whitney U**: Non-parametric alternative to t-test
- **ANOVA**: Compare means across multiple groups`,
  codeExamples: [
    {
      title: 'A/B Test Analysis',
      language: 'python',
      code: `import numpy as np
from scipy import stats

# Sample data
control_rate, treatment_rate = 0.10, 0.12
n_control, n_treatment = 1000, 1000

# Two-proportion z-test
p_pool = (control_rate * n_control + treatment_rate * n_treatment) / (n_control + n_treatment)
se = np.sqrt(p_pool * (1 - p_pool) * (1/n_control + 1/n_treatment))
z = (treatment_rate - control_rate) / se
p_value = 2 * (1 - stats.norm.cdf(abs(z)))

# Confidence interval
diff = treatment_rate - control_rate
se_diff = np.sqrt(control_rate * (1-control_rate)/n_control + 
                  treatment_rate * (1-treatment_rate)/n_treatment)
ci = (diff - 1.96 * se_diff, diff + 1.96 * se_diff)

print(f"Lift: {(diff/control_rate)*100:.1f}%")
print(f"P-value: {p_value:.4f}")
print(f"95% CI: [{ci[0]:.4f}, {ci[1]:.4f}]")`,
      explanation: 'Calculate statistical significance and confidence intervals for an A/B test.'
    }
  ],
  quiz: {
    questions: [
      {
        id: 'stats-q1',
        question: 'What is a Type I error?',
        options: [
          'Failing to reject a false null hypothesis',
          'Rejecting a true null hypothesis',
          'Correct acceptance of null hypothesis',
          'Insufficient sample size'
        ],
        correctAnswer: 1,
        explanation: 'Type I error is rejecting the null hypothesis when it is actually true.'
      },
      {
        id: 'stats-q2',
        question: 'Why is sample size important in A/B testing?',
        options: [
          'Larger samples are always better regardless of cost',
          'It determines statistical power to detect effects',
          'It has no impact on results',
          'Smaller samples give more accurate results'
        ],
        correctAnswer: 1,
        explanation: 'Sample size directly affects the statistical power of detecting true effects.'
      },
      {
        id: 'stats-q3',
        question: 'What does a p-value of 0.03 mean?',
        options: [
          'There is a 3% chance the treatment works',
          'The effect size is 3%',
          '3% probability of observing results if null is true',
          'The test has 3% power'
        ],
        correctAnswer: 2,
        explanation: 'P-value is the probability of observing results as extreme if the null hypothesis is true.'
      },
      {
        id: 'stats-q4',
        question: 'How do you correct for multiple testing?',
        options: [
          'Ignore the problem',
          'Use Bonferroni correction or FDR control',
          'Run more tests',
          'Reduce sample size'
        ],
        correctAnswer: 1,
        explanation: 'Bonferroni and FDR control methods adjust significance thresholds for multiple tests.'
      },
      {
        id: 'stats-q5',
        question: 'What is statistical power?',
        options: [
          'The probability of a Type I error',
          'The probability of detecting a true effect',
          'The sample size required',
          'The p-value threshold'
        ],
        correctAnswer: 1,
        explanation: 'Statistical power is the probability of correctly rejecting a false null hypothesis.'
      }
    ],
    passThreshold: 70
  },
  practice: {
    task: 'Calculate if an A/B test result is statistically significant.',
    starter: `def is_significant(control_conv, control_n, treat_conv, treat_n, alpha=0.05):
    """
    Return True if the difference is statistically significant.
    """
    # Your code here
    pass`,
    solution: `from scipy import stats
import numpy as np

def is_significant(control_conv, control_n, treat_conv, treat_n, alpha=0.05):
    p1, p2 = control_conv/control_n, treat_conv/treat_n
    p_pool = (control_conv + treat_conv) / (control_n + treat_n)
    se = np.sqrt(p_pool * (1 - p_pool) * (1/control_n + 1/treat_n))
    z = (p2 - p1) / se
    p_value = 2 * (1 - stats.norm.cdf(abs(z)))
    return p_value < alpha`,
    hints: [
      'Calculate pooled probability first',
      'Use the z-test formula for proportions',
      'Compare p-value to alpha threshold'
    ]
  }
};
